import { TranslationKeys } from 'localization';
import { WorkOrderModuleCategoriesSettings } from 'lane-shared/domains/workOrder';
import { CoverageHoursEntry } from './useCoverageHours';

export type DropdownOption = { label: string; value: string };

export const ALL_ISSUE_TYPES_VALUE = 'ALL_ISSUE_TYPES';
export const NONE_ISSUE_TYPE_VALUE = 'NONE';

function issueStringsFor(
  category: WorkOrderModuleCategoriesSettings
): string[] {
  return (category.issues ?? []).filter(i => (i ?? '').trim() !== '');
}

// A category's own coverage-hours entries, split into the issues it already
// has a routing for and whether its category-level (no-issue) routing exists.
function existingCoverageForCategory(
  categoryName: string,
  existingEntries: CoverageHoursEntry[]
): { existingIssues: Set<string>; noneAlreadyUsed: boolean } {
  const entriesForCategory = existingEntries.filter(
    e => e.category === categoryName
  );

  const existingIssues = new Set(
    entriesForCategory
      .map(e => e.issue)
      .filter((issue): issue is string => issue !== null)
  );
  const noneAlreadyUsed = entriesForCategory.some(e => e.issue === null);

  return { existingIssues, noneAlreadyUsed };
}

// How many more routings could still be created for this category: every
// configured issue without a routing yet, plus the category-level (no-issue)
// slot if that hasn't been used either. A category with 0 here has nothing
// left to offer and should disappear from the Category dropdown.
export function countCreatableOptionsForCategory(
  category: WorkOrderModuleCategoriesSettings,
  existingEntries: CoverageHoursEntry[]
): number {
  const name = category.firendlyName ?? '';

  if (name.trim() === '') {
    return 0;
  }

  const { existingIssues, noneAlreadyUsed } = existingCoverageForCategory(
    name,
    existingEntries
  );

  const creatableIssueCount = issueStringsFor(category).filter(
    issue => !existingIssues.has(issue)
  ).length;

  return creatableIssueCount + (noneAlreadyUsed ? 0 : 1);
}

// Whether this category's category-level (no-issue) routing already exists -
// exposed so a caller can decide whether an "All Issue Types" save should
// also fold in a None creation, without duplicating the lookup itself.
export function isNoneAlreadyUsed(
  category: WorkOrderModuleCategoriesSettings,
  existingEntries: CoverageHoursEntry[]
): boolean {
  return existingCoverageForCategory(category.firendlyName ?? '', existingEntries)
    .noneAlreadyUsed;
}

// "All Issue Types" is the catch-all: if the category-level (no-issue)
// routing doesn't exist yet either, it should be folded into the same save
// rather than left as a separate option the user has to come back for.
export function shouldCreateNoneWithAllIssueTypes(
  issueTypeValue: string,
  selectedCategory: WorkOrderModuleCategoriesSettings | undefined,
  existingEntries: CoverageHoursEntry[]
): boolean {
  return (
    issueTypeValue === ALL_ISSUE_TYPES_VALUE &&
    selectedCategory !== undefined &&
    !isNoneAlreadyUsed(selectedCategory, existingEntries)
  );
}

export function getIssueTypeInfoKey(
  issueTypeValue: string
): TranslationKeys | null {
  if (issueTypeValue === ALL_ISSUE_TYPES_VALUE) {
    return 'web.admin.serviceRequest.settings.coverageHours.create.allIssueTypesInfo';
  }

  if (issueTypeValue === NONE_ISSUE_TYPE_VALUE) {
    return 'web.admin.serviceRequest.settings.coverageHours.create.noneIssueTypeInfo';
  }

  return null;
}

export function buildCategoryItems(
  categories: WorkOrderModuleCategoriesSettings[],
  existingEntries: CoverageHoursEntry[]
): DropdownOption[] {
  return categories
    .filter(c => (c.firendlyName ?? '').trim() !== '')
    .filter(c => countCreatableOptionsForCategory(c, existingEntries) > 0)
    .map(c => ({ label: c.firendlyName, value: c.firendlyName }));
}

export function findSelectedCategory(
  categories: WorkOrderModuleCategoriesSettings[],
  categoryValue: string | null
): WorkOrderModuleCategoriesSettings | undefined {
  return categories.find(c => c.firendlyName === categoryValue);
}

// An empty issues list means a single category-level routing with no issue —
// this is the exact contract InputCreateCoverageHours.issues documents.
// "All Issue Types" resolves to only the still-creatable issues: submitting
// one that already has a routing is exactly the failure this filtering
// feature exists to prevent.
export function resolveIssues(
  issueTypeValue: string,
  selectedCategory: WorkOrderModuleCategoriesSettings | undefined,
  existingEntries: CoverageHoursEntry[]
): string[] {
  if (issueTypeValue === NONE_ISSUE_TYPE_VALUE) {
    return [];
  }

  if (issueTypeValue === ALL_ISSUE_TYPES_VALUE) {
    if (!selectedCategory) {
      return [];
    }

    const { existingIssues } = existingCoverageForCategory(
      selectedCategory.firendlyName ?? '',
      existingEntries
    );

    return issueStringsFor(selectedCategory).filter(
      issue => !existingIssues.has(issue)
    );
  }

  return [issueTypeValue];
}

export function buildIssueTypeItems(
  selectedCategory: WorkOrderModuleCategoriesSettings | undefined,
  existingEntries: CoverageHoursEntry[],
  t: (key: string) => string
): DropdownOption[] {
  if (!selectedCategory) {
    return [];
  }

  const { existingIssues, noneAlreadyUsed } = existingCoverageForCategory(
    selectedCategory.firendlyName ?? '',
    existingEntries
  );

  const creatableIssues = issueStringsFor(selectedCategory).filter(
    issue => !existingIssues.has(issue)
  );

  const items: DropdownOption[] = [];

  // Once any specific issue has a routing, "All Issue Types" would no
  // longer mean all of them - so it's offered only while none are used yet.
  if (existingIssues.size === 0 && creatableIssues.length > 1) {
    items.push({
      label: t(
        'web.admin.serviceRequest.settings.coverageHours.create.allIssueTypes'
      ),
      value: ALL_ISSUE_TYPES_VALUE,
    });
  }

  if (!noneAlreadyUsed) {
    items.push({
      label: t(
        'web.admin.serviceRequest.settings.coverageHours.create.none'
      ),
      value: NONE_ISSUE_TYPE_VALUE,
    });
  }

  items.push(...creatableIssues.map(issue => ({ label: issue, value: issue })));

  return items;
}
